import { apiClient, isSupabaseConfigured, demoEmployees, clearCache } from './_shared.js'

// ============================================
// PROFILES / TEAM
// ============================================

export async function getTeamMembers(orgId) {
  if (!orgId) return []
  return await apiClient(`/employees/team?orgId=${orgId}`)
}

export async function getEmployeesList(pvzId, orgId) {
  if (!isSupabaseConfigured) return demoEmployees
  const params = new URLSearchParams()
  if (pvzId) params.append('pvzId', pvzId)
  if (orgId) params.append('orgId', orgId)
  const data = await apiClient('/employees?' + params.toString())
  return data.map(emp => ({ ...emp, name: emp.full_name }))
}

export async function getSalaryCandidates(pvzId, orgId) {
  if (!isSupabaseConfigured) return demoEmployees
  const params = new URLSearchParams()
  if (pvzId) params.append('pvzId', pvzId)
  if (orgId) params.append('orgId', orgId)
  const data = await apiClient('/employees/salary-candidates?' + params.toString())
  return data.map(emp => ({ ...emp, name: emp.full_name }))
}

export async function getAllEmployeesList(pvzId, orgId) {
  if (!isSupabaseConfigured) {
    return demoEmployees
  }

  if (!pvzId && !orgId) return []

  const params = new URLSearchParams()
  if (pvzId) params.append('pvzId', pvzId)
  if (orgId) params.append('orgId', orgId)

  try {
    const data = await apiClient('/employees?' + params.toString())
    console.log('[getAllEmployeesList] fetched:', data)
    
    const results = (data || []).map(p => ({ 
      id: p.id, 
      name: p.full_name, 
      full_name: p.full_name,
      role: p.role, 
      is_legacy: p.is_legacy,
      pvz_id: p.pvz_id,
      org_id: p.org_id,
      hired_at: p.hired_at,
      pay_day: p.pay_day,
      employment_type: p.employment_type || 'regular',
      payout_frequency_override: p.payout_frequency_override || null,
    }))
    return results
  } catch (err) {
    console.error('[getAllEmployeesList] error:', err)
    return []
  }
}

export async function setEmployeePassword(employeeId, newPassword) {
  if (!isSupabaseConfigured) return { success: true }
  return apiClient(`/employees/${employeeId}/password`, {
    method: 'POST',
    body: JSON.stringify({ newPassword })
  })
}

export async function approveUser(userId) {
  await apiClient(`/employees/approve/${userId}`, {
    method: 'POST'
  })
  clearCache('team_members')
  clearCache('employees_list')
}

export async function updateUserRole(userId, newRole) {
  await apiClient(`/employees/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role: newRole })
  })
  clearCache('team_members')
  clearCache('employees_list')
}

export async function removeUser(userId) {
  await apiClient(`/employees/${userId}`, { method: 'DELETE' })
  clearCache('team_members')
  clearCache('employees_list')
}

/**
 * Создание "заглушечного" сотрудника без необходимости его регистрации.
 * Такой профиль сразу появляется в графике, расчёте ЗП и операциях.
 * Позже сотрудник может зарегистрироваться и "привязать" свой аккаунт.
 */
export async function createPlaceholderEmployee({ fullName, role, orgId, pvzId, hiredAt }) {
  const data = await apiClient('/employees/placeholder', {
    method: 'POST',
    body: JSON.stringify({
      id: crypto.randomUUID(),
      full_name: fullName,
      role: role || 'employee',
      org_id: orgId,
      pvz_id: pvzId,
      hired_at: hiredAt || new Date().toISOString().split('T')[0]
    })
  })
  clearCache('team_members')
  clearCache('employees_list')
  return data
}

export async function updateProfile(userId, updates) {
  await apiClient(`/employees/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  })
  clearCache('team_members')
  clearCache('employees_list')
}

export async function deleteAuthUser(userId) {
  await apiClient(`/employees/${userId}`, {
    method: 'DELETE'
  })
  clearCache('team_members')
  clearCache('employees_list')
}

export async function updateOrgRolePermissions(orgId, role, permissions) {
  if (!isSupabaseConfigured) return
  await apiClient(`/organizations/${orgId}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ role, permissions })
  })
}

export async function updateOrganization(orgId, updates) {
  if (!isSupabaseConfigured) return
  await apiClient(`/organizations/${orgId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  })
}

export async function getPvzDetails(pvzId) {
  return await apiClient(`/pvz/${pvzId}`)
}

export async function updatePvz(pvzId, updates) {
  if (!isSupabaseConfigured) return
  await apiClient(`/pvz/${pvzId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  })
}

export async function deletePvz(pvzId) {
  if (!isSupabaseConfigured) return
  await apiClient(`/pvz/${pvzId}`, {
    method: 'DELETE'
  })
}
